import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'
import type {OptimisticTask} from '../types/task'
import {
  DEFAULT_MCP_PERMISSION_MODE,
  type CreateAPITaskParams,
  type APITask,
  type CreateAPITaskMutationParams,
} from '../types/api-task'
import {fetchCAPI} from '../utils/fetch-capi'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'
import {repoTasksApiPath} from '../utils/api-url-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import {extractImageUrls} from '../utils/markdown-images'
import {readTasks, writeTasks, type TaskListPage} from './repo-task-utils'

/**
 * Response shape for create task.
 * Legacy API wraps in {task: APITask}; new API may return the task at root level.
 */
interface CreateAPITaskResponse {
  task?: APITask
  // New API returns task fields at root level
  id?: string
  [key: string]: unknown
}

interface CreateAPITaskMutationResult {
  task: APITask
  clientGeneratedId: string
}

export interface UseCreateAPITaskOptions {
  onError?: (error: Error, variables: CreateAPITaskMutationParams) => void

  /**
   * Called after a task is successfully created (for both the async and
   * fire-and-forget mutate paths, including the failure-banner retry). Lets
   * callers run side effects like navigation that must fire on every success,
   * not just the awaited initial submission.
   */
  onSuccess?: () => void

  /** Query key to use for optimistic updates. */
  queryKey: readonly unknown[]
  /**
   * Additional query keys to invalidate (but not optimistically update) after a
   * successful creation. Used to refresh sibling caches with a different shape —
   * e.g. the grouped-tasks sidebar cache, which can't share the flat optimistic path.
   */
  additionalInvalidateKeys?: ReadonlyArray<readonly unknown[]>
  /**
   * The page size for pagination. Used to truncate the list after replacing
   * the optimistic task with the real one which prevents task overflow.
   */
  pageSize: number
}

export function useCreateAPITask({
  onError,
  onSuccess,
  queryKey,
  additionalInvalidateKeys,
  pageSize,
}: UseCreateAPITaskOptions) {
  const queryClient = useQueryClient()

  const queryFilter = {queryKey, exact: false} as const

  const createTaskMutation = useMutation<CreateAPITaskMutationResult, Error, CreateAPITaskMutationParams>({
    mutationKey: ['createAPITask'],
    onMutate: async (params: CreateAPITaskMutationParams) => {
      // Cancel any existing refetches across all matching cache entries
      await queryClient.cancelQueries(queryFilter)

      const matches = queryClient.getQueriesData<TaskListPage>(queryFilter)
      const optimisticTask = createOptimisticTask(params)

      // Mirror the optimistic task into each matching cache entry (unscoped + scoped).
      for (const [cacheKey, previousData] of matches) {
        const isScoped = cacheKey.length > queryKey.length
        const withOptimistic = [optimisticTask, ...readTasks(previousData)]
        const nextTasks = isScoped ? withOptimistic : withOptimistic.slice(0, pageSize)
        queryClient.setQueryData<TaskListPage>(cacheKey, writeTasks(previousData, nextTasks))
      }

      return {previousMatches: matches}
    },
    mutationFn: async (params: CreateAPITaskMutationParams): Promise<CreateAPITaskMutationResult> => {
      const imageCount = extractImageUrls(params.problemStatement).length

      sendEvent('copilot.agent_session.job_creation_requested', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
        image_count: imageCount,
      })

      // Default to not create PRs unless explicitly specified (decoupled mode)
      if (params.createPullRequest === undefined) {
        params.createPullRequest = false
      }

      const response = await createTask(params)

      if (!response.ok) {
        sendEvent('copilot.agent_session.job_creation_failed', {
          event_identifiers: params.clientGeneratedId,
          event_type: params.eventType,
          nwo: params.nwo,
          status: response.status,
          error: response.statusText,
          image_count: imageCount,
        })
        throw new Error(`Failed to create task: ${params.clientGeneratedId}`)
      }

      const data: CreateAPITaskResponse = await response.json()

      // Support both response shapes:
      // - Legacy: { task: { id, ... } }
      // - New (REST style): { id, ... } (task at root level)
      const task: APITask = data.task ? {...data.task, name: data.name as string} : (data as unknown as APITask)

      sendEvent('copilot.agent_session.job_creation_completed', {
        event_identifiers: params.clientGeneratedId,
        event_type: params.eventType,
        nwo: params.nwo,
        task_id: task.id,
        image_count: imageCount,
      })

      return {task, clientGeneratedId: params.clientGeneratedId}
    },
    onError: (error, variables) => {
      // Remove the failed optimistic task from every cache entry it was mirrored into.
      queryClient.setQueriesData<TaskListPage>(queryFilter, oldData =>
        writeTasks(
          oldData,
          readTasks(oldData).filter(task => task.clientGeneratedId !== variables.clientGeneratedId),
        ),
      )

      onError?.(error, variables)
    },
    onSuccess: ({task, clientGeneratedId}) => {
      // Replace the optimistic task with the real server task in every matching cache entry.
      const transformedTask = transformAPITaskToUnhydrated(task)
      const matches = queryClient.getQueriesData<TaskListPage>(queryFilter)
      for (const [cacheKey, oldData] of matches) {
        // Only the unscoped (default) cache entry gets truncated to `pageSize` — scoped
        // consumers (e.g. the sidebar with a growing pageSize) manage their own page
        // length and would be incorrectly shrunk if we truncated them here.
        const isScoped = cacheKey.length > queryKey.length
        const cachedTasks = readTasks(oldData)
        const withoutOptimistic = cachedTasks.filter(t => t.clientGeneratedId !== clientGeneratedId)
        const sorted = transformedTask ? sortTasksByDate([transformedTask, ...withoutOptimistic]) : withoutOptimistic
        const finalList = isScoped ? sorted : sorted.slice(0, pageSize)
        queryClient.setQueryData<TaskListPage>(cacheKey, writeTasks(oldData, finalList))
      }

      queryClient.invalidateQueries({queryKey, exact: true})

      for (const key of additionalInvalidateKeys ?? []) {
        queryClient.invalidateQueries({queryKey: key})
      }

      // Run the consumer callback (e.g. navigation) in a guard: this handler is
      // awaited by TanStack Query, so a throw here would flip the already-successful
      // mutation into onError — rolling back the real task and showing the failure
      // banner even though the server created the task (and inviting a duplicate on retry).
      try {
        onSuccess?.()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('useCreateAPITask onSuccess callback threw', error)
      }
    },
  })

  const {mutate, mutateAsync} = createTaskMutation

  const createAPITask = useCallback(
    (params: CreateAPITaskParams, clientTaskId?: string) => {
      const clientGeneratedId = clientTaskId ?? uuid()
      mutate({...params, clientGeneratedId})
    },
    [mutate],
  )

  const createAPITaskAsync = useCallback(
    async (params: CreateAPITaskParams, clientTaskId?: string): Promise<CreateAPITaskMutationResult> => {
      const clientGeneratedId = clientTaskId ?? uuid()
      return mutateAsync({...params, clientGeneratedId})
    },
    [mutateAsync],
  )

  return {
    createTask: createAPITask,
    createTaskAsync: createAPITaskAsync,
    isCreating: createTaskMutation.isPending,
    createError: createTaskMutation.error,
  }
}

/**
 * Helper function to create task via the Tasks API
 */
export async function createTask({
  nwo,
  problemStatement,
  model,
  reasoningEffort,
  agentId,
  customAgent,
  baseRef,
  createPullRequest,
  mcpPermissionMode = DEFAULT_MCP_PERMISSION_MODE,
  eventType,
  eventContent,
  eventIdentifiers,
  clientGeneratedId,
  endpoint = undefined,
}: CreateAPITaskMutationParams): Promise<Response> {
  const path = endpoint ?? repoTasksApiPath(nwo)

  // Convert agentId string to number for API
  let agentIdNumber: number | undefined = undefined
  if (agentId !== undefined && agentId !== null) {
    agentIdNumber = parseInt(agentId, 10)
    if (isNaN(agentIdNumber)) {
      throw new Error(`Invalid agentId: "${agentId}". agentId must be a valid numeric string.`)
    }
  }

  const allEventIdentifiers = [...(eventIdentifiers || []), clientGeneratedId]
  return await fetchCAPI({
    path,
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      event_identifiers: allEventIdentifiers,
      ...(model && {model}),
      ...(reasoningEffort && {reasoning_effort: reasoningEffort}),
      ...(agentIdNumber && {agent_id: agentIdNumber}),
      ...(customAgent && {custom_agent: customAgent}),
      ...(baseRef && {base_ref: baseRef}),
      ...(createPullRequest !== undefined && {create_pull_request: createPullRequest}),
      ...(mcpPermissionMode !== DEFAULT_MCP_PERMISSION_MODE && {permission_mode: mcpPermissionMode}),
      ...(eventType && {event_type: eventType}),
      ...(eventContent && {event_content: eventContent}),
    },
  })
}

function createOptimisticTask({
  clientGeneratedId,
  nwo,
  problemStatement,
  customAgent,
  agentId,
}: CreateAPITaskMutationParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1, // Placeholder until we get real ID from server
    resource_global_id: '', // Placeholder until we get real ID from server
    session_count: 1,
    created_at: new Date().toISOString(),
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    nwo,
    problemStatement,
    name: problemStatement,
    custom_agent: customAgent,
    agentId: agentId?.toString(),
  }
}
